import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { History as HistoryIcon, Search, Download, Image as ImageIcon, Music, Code2, Layers, Pin } from 'lucide-react';
import api from '../lib/api';
import { cn, downloadBlob } from '../lib/utils';
import { useToast } from '../components/ui/Toast';
import PageHeader from '../components/ui/PageHeader';
import GlassCard from '../components/ui/GlassCard';
import Button from '../components/ui/Button';
import Skeleton from '../components/ui/Skeleton';
import GenerationCard from '../components/history/GenerationCard';

const FILTERS = [
  { value: 'ALL', label: 'All', icon: Layers },
  { value: 'IMAGE', label: 'Images', icon: ImageIcon },
  { value: 'MUSIC', label: 'Music', icon: Music },
  { value: 'CODE', label: 'Code', icon: Code2 },
];

export default function History() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [modality, setModality] = useState('ALL');
  const [pinnedOnly, setPinnedOnly] = useState(false);

  // Debounce search input.
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const filters = useMemo(
    () => ({ q, modality: modality === 'ALL' ? undefined : modality, pinned: pinnedOnly ? 'true' : undefined }),
    [q, modality, pinnedOnly]
  );

  const queryKey = ['history', filters];
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey,
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      const params = { ...filters, limit: 18, ...(pageParam ? { cursor: pageParam } : {}) };
      Object.keys(params).forEach((k) => params[k] === undefined && delete params[k]);
      return (await api.get('/history', { params })).data;
    },
    getNextPageParam: (last) => last.nextCursor || undefined,
  });

  const items = data?.pages.flatMap((p) => p.items) || [];

  const patchCache = (updater) =>
    qc.setQueryData(queryKey, (old) =>
      old ? { ...old, pages: old.pages.map((pg) => ({ ...pg, items: updater(pg.items) })) } : old
    );

  const onPin = async (g) => {
    const next = !g.pinned;
    patchCache((list) => list.map((it) => (it.id === g.id ? { ...it, pinned: next } : it)));
    try {
      await api.patch(`/history/${g.id}/pin`, { pinned: next });
      toast.success(next ? 'Pinned' : 'Unpinned');
    } catch {
      toast.error('Could not update pin.');
    } finally {
      qc.invalidateQueries({ queryKey: ['history'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    }
  };

  const onDelete = async (g) => {
    patchCache((list) => list.filter((it) => it.id !== g.id));
    try {
      await api.delete(`/history/${g.id}`);
      toast.success('Deleted');
    } catch {
      toast.error('Could not delete.');
      qc.invalidateQueries({ queryKey: ['history'] });
    } finally {
      qc.invalidateQueries({ queryKey: ['stats'] });
    }
  };

  const exportAll = () => {
    if (!items.length) return;
    const blob = new Blob([JSON.stringify(items, null, 2)], { type: 'application/json' });
    downloadBlob(blob, `aether-history-${Date.now()}.json`);
    toast.success(`Exported ${items.length} items`);
  };

  return (
    <div>
      <PageHeader
        title="History"
        subtitle="Search, pin, export, and revisit everything you've made."
        icon={HistoryIcon}
        actions={
          <Button variant="secondary" size="sm" icon={Download} onClick={exportAll} disabled={!items.length}>
            Export all
          </Button>
        }
      />

      {/* Controls */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-content-subtle" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search prompts…"
            className="glass h-11 w-full rounded-lg pl-10 pr-3 text-sm placeholder:text-content-subtle focus-visible:ring-2 focus-visible:ring-brand-400"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setModality(f.value)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors',
                modality === f.value
                  ? 'border-brand-400 bg-brand-500/10 text-content'
                  : 'border-border/70 text-content-muted hover:text-content'
              )}
            >
              <f.icon className="h-4 w-4" /> {f.label}
            </button>
          ))}
          <button
            onClick={() => setPinnedOnly((p) => !p)}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm transition-colors',
              pinnedOnly
                ? 'border-amber-400 bg-amber-400/10 text-amber-200'
                : 'border-border/70 text-content-muted hover:text-content'
            )}
          >
            <Pin className="h-4 w-4" /> Pinned
          </button>
        </div>
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <GlassCard key={i} className="p-4">
              <Skeleton className="aspect-video w-full" />
              <Skeleton className="mt-3 h-4 w-3/4" />
            </GlassCard>
          ))}
        </div>
      ) : items.length === 0 ? (
        <GlassCard className="flex flex-col items-center justify-center gap-3 p-16 text-center">
          <HistoryIcon className="h-8 w-8 text-content-subtle" />
          <p className="font-medium">No results</p>
          <p className="max-w-xs text-sm text-content-muted">
            {q || pinnedOnly || modality !== 'ALL'
              ? 'Try a different search or filter.'
              : 'Generate something and it will show up here.'}
          </p>
        </GlassCard>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((g, i) => (
              <GenerationCard key={g.id} generation={g} index={i} onPin={onPin} onDelete={onDelete} />
            ))}
          </div>
          {hasNextPage && (
            <div className="mt-8 flex justify-center">
              <Button variant="secondary" loading={isFetchingNextPage} onClick={() => fetchNextPage()}>
                Load more
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
